import { isScienceExpert, programLabel } from '@/lib/programs'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ReviewFilters, type ReviewFiltersValue } from '@/components/reviews/ReviewFilters'
import { api } from '@/api/client'
import { fmtDateTime } from '@/lib/format'
import { useReviewQueue } from '@/lib/useReviewQueue'
import { useProfile } from '@/auth/ProfileContext'
import type { Round } from '@/components/reviews/types'

export default function Reviews() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ReviewFiltersValue>({ kind: '', program: '' })
  const { kind, program } = filters
  const mine = params.get('mine') !== '0'
  const { profile } = useProfile()
  const scienceReviewer = isScienceExpert(profile)
  useEffect(() => { setFilters({ kind: '', program: '' }) }, [scienceReviewer])
  const reviewerScope = scienceReviewer ? 'physics' : 'engineering'
  // Every queue is filtered to the expert's side by the server (reviews.permissions.can_review).
  const submissions = useReviewQueue('submission', program, false, !!profile)
  const quality = useReviewQueue('quality', program, false, !!profile, reviewerScope)
  const mySubmissions = useReviewQueue('submission', program, true, !!profile)
  const myQuality = useReviewQueue('quality', program, true, !!profile, reviewerScope)
  const science = useReviewQueue('scientific', program, false, scienceReviewer)
  const myScience = useReviewQueue('scientific', program, true, scienceReviewer)
  const show = (value: string) => !kind || kind === value
  const queues = [
    ...(show('submission') ? [mySubmissions, ...(!mine ? [submissions] : [])] : []),
    ...(show('quality') || kind === 'scientific' ? [myQuality, ...(!mine ? [quality] : [])] : []),
    ...(scienceReviewer && show('scientific') ? [myScience, ...(!mine ? [science] : [])] : []),
  ]
  const owned = new Set([...mySubmissions.rows, ...myQuality.rows, ...myScience.rows].map((row) => row.round_id))
  const rows = queues
    .flatMap((queue) => queue.rows)
    .filter(() => !!profile)
    .filter(row => kind !== 'scientific' || row.kind === 'scientific' || !!row.scientific_scope)
    .sort((a, b) => Number(!!a.my_review_completed) - Number(!!b.my_review_completed) || b.created_at.localeCompare(a.created_at))
  const busy = queues.some((queue) => queue.busy)
  const [claiming, setClaiming] = useState('')
  const [error, setError] = useState('')
  function patch(values: Record<string, string>) {
    const next = new URLSearchParams(params)
    next.delete('kind')
    next.delete('program')
    Object.entries(values).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)))
    setParams(next)
  }
  async function claim(row: Round) {
    setClaiming(row.round_id)
    setError('')
    try {
      await api.post(`/tasks/${row.task_id}/reviews/${row.round_id}/claim`)
      navigate(`/sme/reviews/${row.task_id}/${row.round_id}`)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setClaiming('')
    }
  }
  return (
    <div className="review-queue-page" data-tour="review-queue">
      <div className="page-head">
        <h1>Reviews</h1>
      </div>
      <div className="peer-review-tabs review-queue-types" role="group" aria-label="Show reviews">
        <button aria-pressed={mine} onClick={() => patch({ mine: '1' })}>
          My reviews
        </button>
        <button aria-pressed={!mine} onClick={() => patch({ mine: '0' })}>
          All reviews
        </button>
      </div>
      <div className="review-queue-filters">
        <ReviewFilters value={filters} onChange={setFilters} science={scienceReviewer} />
      </div>
      {error && (
        <p className="note err" role="alert">
          {error}
        </p>
      )}
      {queues.map(
        (queue, index) =>
          queue.error && (
            <p key={index} className="note err" role="alert">
              {queue.error}{' '}
              <button className="link small" onClick={() => void queue.load(queue.cursor ?? undefined)}>
                Retry
              </button>
            </p>
          ),
      )}
      <section className="review-queue-list" aria-label={mine ? 'My reviews' : 'All reviews'}>
        {rows.map((row) => (
          <article key={row.round_id} className="review-queue-row">
            <div>
              <h2>{row.title}</h2>
              <div className="review-queue-meta muted small">
                <span className={`chip review-kind ${row.kind === 'quality' ? 'quality' : ''}`}>
                  {row.scientific_scope ? 'Quality check' : { quality: 'Quality check', scientific: 'Scientific workflow', submission: 'Peer review' }[row.kind]}
                </span>
                {row.status === 'deciding' && <span className="chip">{row.handoff_error ? 'Submission needs attention' : 'Submitting'}</span>}
                {row.my_review_completed && <span className="chip">Completed</span>}
                <span>{programLabel(row)}</span>
                <span>{row.author_email}</span>
                <time dateTime={row.created_at}>{fmtDateTime(row.created_at)}</time>
              </div>
            </div>
            {row.kind === 'scientific' || row.scientific_scope ? (
              <Link className="btn sm primary" to={`/sme/tasks/${row.task_id}`}>
                {owned.has(row.round_id) ? 'Continue review' : 'Open workflow'}
              </Link>
            ) : owned.has(row.round_id) ? (
              <Link
                className={`btn sm${row.my_review_completed ? '' : ' primary'}`}
                to={`/sme/reviews/${row.task_id}/${row.round_id}`}
              >
                {row.my_review_completed ? 'View review' : 'Continue review'}
              </Link>
            ) : (
              <button className="sm" disabled={!!claiming} onClick={() => void claim(row)}>
                {claiming === row.round_id ? 'Opening…' : 'Start review'}
              </button>
            )}
          </article>
        ))}
        {!rows.length && !queues.some((queue) => queue.error) && (
          <div className="review-queue-empty">
            <p className="muted small">
              {busy
                ? 'Loading reviews…'
                : mine
                  ? 'No reviews in progress.'
                  : 'No reviews waiting right now.'}
            </p>
            {!busy && mine && (
              <button className="link small" onClick={() => patch({ mine: '0' })}>
                Browse all reviews
              </button>
            )}
          </div>
        )}
        {!!rows.length && busy && <p className="muted small" role="status">Loading remaining reviews…</p>}
      </section>
    </div>
  )
}
